// Google Vision API integration for image label extraction
// This will help automatically detect communities from image content

export interface VisionLabel {
  description: string;
  score: number;
  topicality: number;
}

export interface VisionApiResponse {
  labels: VisionLabel[];
  error?: string;
}

/**
 * Extract labels from an image using Google Vision API
 * This function calls the existing Supabase Edge Function for vision analysis
 */
export async function extractImageLabels(imageUrl: string): Promise<VisionApiResponse> {
  try {
    // Call existing vision-labeler Edge Function
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vision-labeler`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ 
        image_url: imageUrl,
        features: ['labels', 'objects'] 
      })
    });

    if (!response.ok) {
      console.error('Vision API error:', response.statusText);
      return { labels: [], error: 'Failed to analyze image' };
    }

    const data = await response.json();
    
    // Transform the response to match our interface
    const labels: VisionLabel[] = [];
    
    if (data.detailed_analysis?.labels) {
      labels.push(...data.detailed_analysis.labels.map((label: any) => ({
        description: label.description,
        score: label.score,
        topicality: label.confidence / 100
      })));
    }
    
    if (data.detailed_analysis?.objects) {
      labels.push(...data.detailed_analysis.objects.map((obj: any) => ({
        description: obj.name,
        score: obj.score,
        topicality: obj.score
      })));
    }
    
    return {
      labels,
      error: data.error
    };
  } catch (error) {
    console.error('Error calling Vision API:', error);
    return { labels: [], error: 'Failed to connect to vision service' };
  }
}

/**
 * Fix known Vision API misclassifications
 * Prevents future incorrect labels from being stored
 */
function fixMisclassifications(label: string): string {
  const lower = label.toLowerCase();
  
  // Vision API sometimes misclassifies flower petals as animals
  if (lower === 'animal' || lower === 'animals') {
    return 'petals';
  }
  
  // Add more known misclassifications here as needed
  
  return label;
}

/**
 * Process vision labels for community detection
 * Filters and normalizes labels for better matching
 */
export function processVisionLabels(labels: VisionLabel[]): string[] {
  return labels
    .filter(label => label.score > 0.5) // Only confident labels
    .map(label => fixMisclassifications(label.description.toLowerCase()))
    .slice(0, 10); // Limit to top 10 labels
}

/**
 * Mock function for development/testing when Vision API is not available
 */
export async function mockExtractImageLabels(imageUrl: string): Promise<VisionApiResponse> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Return mock labels based on common image types
  const mockLabels: VisionLabel[] = [];
  
  if (imageUrl.includes('art') || imageUrl.includes('paint')) {
    mockLabels.push(
      { description: 'painting', score: 0.9, topicality: 0.85 },
      { description: 'artwork', score: 0.85, topicality: 0.8 },
      { description: 'canvas', score: 0.7, topicality: 0.65 }
    );
  } else if (imageUrl.includes('photo')) {
    mockLabels.push(
      { description: 'photography', score: 0.9, topicality: 0.9 },
      { description: 'camera', score: 0.75, topicality: 0.7 }
    );
  } else if (imageUrl.includes('game')) {
    mockLabels.push(
      { description: 'video game', score: 0.95, topicality: 0.9 },
      { description: 'gaming', score: 0.85, topicality: 0.85 }
    );
  } else {
    mockLabels.push(
      { description: 'image', score: 0.5, topicality: 0.5 }
    );
  }
  
  return { labels: mockLabels };
}

/**
 * Get image labels with fallback to mock in development
 */
export async function getImageLabels(imageUrl: string): Promise<string[]> {
  // Use mock in development if Vision API not configured
  const isDevelopment = import.meta.env.DEV;
  const hasVisionApi = import.meta.env.VITE_GOOGLE_VISION_ENABLED === 'true';
  
  let response: VisionApiResponse;
  
  if (isDevelopment && !hasVisionApi) {
    response = await mockExtractImageLabels(imageUrl);
  } else {
    response = await extractImageLabels(imageUrl);
  }
  
  if (response.error) {
    console.warn('Vision API error, falling back to empty labels:', response.error);
    return [];
  }
  
  return processVisionLabels(response.labels);
}
